import React from "react"
import _ from "lodash"
import { styled } from "@mui/material/styles"
import { css } from '@emotion/react';

import {
  needsMoreInfoReasons,
  rejectionReasons,
} from "../utils/samplefocusDefaults"
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material"
import { titleize } from "../utils/stringUtils"
import { inject } from "mobx-react"

const SFFormControlLabel = styled(FormControlLabel)`
  align-items: flex-start;

  .MuiCheckbox-root {
    padding-top: 1px;
  }
`

const SFTextField = styled(TextField)`
  margin-bottom: 20px;
`

const SampleEventFormContainer = styled('div')`
  display: flex;
  justify-content: start;
`

const SampleEventFormColumn = styled('div')`
  margin-right: 35px;
  flex-grow: 1;
`

const CenteringContainer = styled('div')`
  display: flex;
  justify-content: center;
`

@inject("sampleStore")
export default class SampleEventForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      needsMoreInfo: [],
      needsMoreInfoCustom: "",
      rejection: [],
      rejectionCustom: "",
      submittingError: false,
      isSubmitting: false,
      hasSubmitted: false,
    }
  }

  handleCheckboxSelect = (event, reasonKey, reasonType) => {
    const didSelect = event.target.checked
    let newState = { ...this.state }
    if (didSelect) {
      newState[reasonType].push(reasonKey)
    } else {
      newState[reasonType] = newState[reasonType].filter(
        (reason) => reason !== reasonKey
      )
    }
    this.setState(newState)
  }

  handleTextFieldChange = (event, reasonType) => {
    const text = event.target.value
    this.setState({ [`${reasonType}Custom`]: text })
  }

  handleSubmit = async (reasonType) => {
    const { sampleStore } = this.props
    const { rejectionCustom, needsMoreInfoCustom } = this.state
    let reasons = this.state[reasonType]
    if (rejectionCustom) {
      reasons.push(rejectionCustom)
    }
    if (needsMoreInfoCustom) {
      reasons.push(needsMoreInfoCustom)
    }


    this.setState({ submittingError: false, isSubmitting: reasonType })

    let didCreateEventResults = []
    await Promise.all(
      sampleStore.selectedSamples.map(async (sample) => {
        const didCreateEvent = await sample.createEvent(reasonType, reasons)
        if (didCreateEvent) {
          sampleStore.removeSample(sample)
        }
        didCreateEventResults.push(didCreateEvent)
      })
    )

    const uniqueEventResults = _.uniq(didCreateEventResults)
    const allEventsCreated =
      uniqueEventResults.length === 1 && uniqueEventResults[0] === true

    if (allEventsCreated) {
      this.setState({ isSubmitting: null, hasSubmitted: true })
    } else {
      this.setState({ isSubmitting: null, submittingError: true })
    }
  }

  renderReasonCheckboxes = (reasonDescription, reasonKey, reasonType) => {
    const reasonTypeLabel = reasonType === "needsMoreInfo" ? "Needs More Info" : "Rejection";
    return (
      <React.Fragment key={reasonKey}>
        <SFFormControlLabel
          aria-labelledby={`checkbox-label-${reasonType}-${reasonKey}`}
          control={
            <Checkbox
              onChange={(e) =>
                this.handleCheckboxSelect(e, reasonKey, reasonType)
              }
              aria-label={`Checkbox option for ${reasonTypeLabel}`}
            />
          }
          label={
            <Tooltip
              placement="right-start"
              title={
                <div
                  css={css`
                    font-size: 14px;
                  `}
                >
                  "{reasonDescription}"
                </div>
              }
            >
              <div id={`checkbox-label-${reasonType}-${reasonKey}`}>{titleize(reasonKey)}</div>
            </Tooltip>
          }
        />
      </React.Fragment>
    )
  }

  renderForm() {
    const {
      needsMoreInfo,
      needsMoreInfoCustom,
      rejection,
      rejectionCustom,
      isSubmitting,
      hasSubmitted,
      submittingError,
    } = this.state

    if (isSubmitting) {
      return (
        <CenteringContainer>
          <CircularProgress />
        </CenteringContainer>
      )
    } else if (hasSubmitted) {
      return (
        <CenteringContainer>
          <h6>Feedback submitted ✅</h6>
        </CenteringContainer>
      )
    } else if (submittingError) {
      return (
        <CenteringContainer>
          <h6>
            Something went wrong. Please refresh the page and try again. If
            multiple samples were selected some feedback may have been
            succesfully submitted.
          </h6>
        </CenteringContainer>
      )
    } else {
      return (
        <SampleEventFormContainer>
          <SampleEventFormColumn>
            <FormGroup>
              {_.map(needsMoreInfoReasons, (reasonDescription, reasonKey) => {
                return this.renderReasonCheckboxes(
                  reasonDescription,
                  reasonKey,
                  "needsMoreInfo"
                )
              })}
            <SFTextField
              label="Custom message to user"
              multiline
              variant="standard"
              fullWidth
              onChange={(e) => this.handleTextFieldChange(e, "needsMoreInfo")}
              value={needsMoreInfoCustom}
              aria-label="Input for a custom message requesting more information from the user"
            />
            </FormGroup>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => this.handleSubmit("needsMoreInfo")}
              disabled={needsMoreInfo.length === 0 && needsMoreInfoCustom.length === 0}
              aria-label="Request more information"
            >
              Needs More Info
            </Button>
          </SampleEventFormColumn>
          <SampleEventFormColumn>
            <FormGroup>
              {_.map(rejectionReasons, (reasonDescription, reasonKey) => {
                return this.renderReasonCheckboxes(
                  reasonDescription,
                  reasonKey,
                  "rejection"
                )
              })}
            <SFTextField
              label="Custom message to user"
              multiline
              variant="standard"
              fullWidth
              onChange={(e) => this.handleTextFieldChange(e, "rejection")}
              value={rejectionCustom}
              aria-label="Input for a custom reason for rejecting the selected samples"
            />
            </FormGroup>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => this.handleSubmit("rejection")}
              disabled={rejection.length === 0 && rejectionCustom.length === 0}
              aria-label="Reject"
            >
              Reject
            </Button>
          </SampleEventFormColumn>
        </SampleEventFormContainer>
      )
    }
  }

  render() {
    return (
      <div>
        <p
          css={css`
            font-weight: 600;
          `}
        >
          Send Feedback
        </p>
        {this.renderForm()}
      </div>
    )
  }
}
